import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "border border-line bg-slate-100/70 px-3 py-2 text-sm text-slate-700 outline-none focus:border-blaze";

export const statusLabel = (j, forContractor = false) => {
  if (j.status === "completed") return { label: "Completed", cls: "border-green-500/50 bg-green-500/10 text-green-700" };
  if (j.status === "cancelled") return { label: "Cancelled", cls: "border-red-500/50 bg-red-500/10 text-red-600" };
  if (j.status === "in_progress") return { label: "In Progress", cls: "border-orange-500/50 bg-orange-500/10 text-orange-700" };
  if (j.status === "confirmed") return { label: "Confirmed", cls: "border-emerald-500/50 bg-emerald-500/10 text-emerald-700" };
  if ((j.assigned_movers || []).length) return { label: "Assigned", cls: "border-blue-500/50 bg-blue-500/10 text-blue-400" };
  if (!forContractor && (j.contractor_id || j.contractor_name)) return { label: "Assigned", cls: "border-blue-500/50 bg-blue-500/10 text-blue-400" };
  return { label: "Unassigned", cls: "border-yellow-500/50 bg-yellow-500/10 text-yellow-700" };
};

export const JobsCalendar = ({ onView, isContractor = false }) => {
  const [date, setDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [jobs, setJobs] = useState([]);
  const [movers, setMovers] = useState([]);
  const [selection, setSelection] = useState({});
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("day");
  const [week, setWeek] = useState(null);

  useEffect(() => {
    axios.get(`${API}/movers`).then(({ data }) => setMovers(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (view !== "week") return;
    axios.get(`${API}/bookings/week`, { params: { date } }).then(({ data }) => setWeek(data)).catch(() => setWeek(null));
  }, [date, view]);

  const shiftWeek = (days) => {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + days);
    setDate(d.toLocaleDateString("en-CA"));
  };

  useEffect(() => {
    axios.get(`${API}/movers`).then(({ data }) => setMovers(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/bookings/by-date`, { params: { date } })
      .then(({ data }) => {
        setJobs(data);
        const sel = {};
        data.forEach((j) => { sel[j.job_id] = j.assigned_movers || []; });
        setSelection(sel);
      })
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, [date]);

  const toggle = (jid, name) =>
    setSelection((s) => ({
      ...s,
      [jid]: (s[jid] || []).includes(name) ? (s[jid] || []).filter((m) => m !== name) : [...(s[jid] || []), name],
    }));

  const assign = async (jid) => {
    try {
      await axios.post(`${API}/bookings/${jid}/assign`, { movers: selection[jid] || [] });
      setJobs((js) => js.map((j) => (j.job_id === jid ? { ...j, assigned_movers: selection[jid] } : j)));
      toast.success(`Movers assigned to ${jid}`);
    } catch {
      toast.error("Could not assign movers.");
    }
  };

  const setStatus = async (jid, status) => {
    try {
      await axios.patch(`${API}/bookings/${jid}`, { status });
      setJobs((js) => js.map((j) => (j.job_id === jid ? { ...j, status } : j)));
      toast.success(`${jid} → ${status}`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  return (
    <div className="mt-8" data-testid="jobs-calendar">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex gap-2" data-testid="calendar-view-toggle">
          {[["day", "Day"], ["week", "Week"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setView(k)}
              data-testid={`view-${k}`}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest ${view === k ? "bg-blaze text-white" : "border border-line text-slate-600 hover:border-blaze hover:text-blaze"}`}
            >
              {l}
            </button>
          ))}
        </div>
        {view === "day" ? (
          <>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Select Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="calendar-date-input" />
            <span className="text-xs text-slate-500" data-testid="calendar-count">{jobs.length} job{jobs.length === 1 ? "" : "s"} booked</span>
          </>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={() => shiftWeek(-7)} className="border border-line px-3 py-2 text-xs text-slate-600 hover:border-blaze hover:text-blaze" data-testid="roster-prev">‹ Prev</button>
            <span className="text-sm text-slate-700" data-testid="roster-range">{week ? `${week.week_start} → ${week.week_end}` : "…"}</span>
            <button onClick={() => shiftWeek(7)} className="border border-line px-3 py-2 text-xs text-slate-600 hover:border-blaze hover:text-blaze" data-testid="roster-next">Next ›</button>
          </div>
        )}
      </div>

      {view === "week" && (
        <div className="mt-6" data-testid="roster-view">
          {!week ? (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blaze" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                {Array.from({ length: 7 }, (_, i) => {
                  const d = new Date(week.week_start + "T00:00:00");
                  d.setDate(d.getDate() + i);
                  const ds = d.toLocaleDateString("en-CA");
                  const dayJobs = week.bookings.filter((b) => b.moving_date === ds);
                  return (
                    <div key={ds} className="min-h-32 border border-line bg-slate-100/70 p-3" data-testid={`roster-day-${i}`}>
                      <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                        {d.toLocaleDateString("en-AU", { weekday: "short" })} <span className="text-slate-700">{d.getDate()}</span>
                      </p>
                      {dayJobs.length === 0 ? (
                        <p className="text-[10px] text-slate-700">—</p>
                      ) : (
                        dayJobs.map((b) => (
                          <button
                            key={b.job_id}
                            onClick={() => onView(b.job_id)}
                            className="mb-1.5 block w-full border border-blaze/40 bg-blaze/10 px-2 py-1.5 text-left transition-colors hover:bg-blaze/20"
                            data-testid={`roster-job-${b.job_id}`}
                          >
                            <p className="text-[10px] font-bold text-blaze">{b.job_id}</p>
                            <p className="text-[10px] text-slate-700">{b.start_time}</p>
                            <p className="text-[10px] text-slate-500">{(b.assigned_movers || []).join(", ") || "Unassigned"}</p>
                          </button>
                        ))
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 border border-line bg-slate-100/70 p-5" data-testid="mover-workload">
                <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-blaze">Mover Workload This Week</p>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                  {movers.map((m) => {
                    const mJobs = week.bookings.filter((b) => (b.assigned_movers || []).includes(m.name));
                    const days = new Set(mJobs.map((b) => b.moving_date)).size;
                    return (
                      <div key={m.id} className="border border-line p-3" data-testid={`workload-${m.name.toLowerCase()}`}>
                        <p className="text-sm font-semibold text-slate-900">{m.name}</p>
                        <p className="text-xs text-slate-500">{mJobs.length} job{mJobs.length === 1 ? "" : "s"} · {days} day{days === 1 ? "" : "s"}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {view === "day" && (loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blaze" /></div>
      ) : jobs.length === 0 ? (
        <p className="mt-8 border border-line/60 p-8 text-center text-sm text-slate-500" data-testid="calendar-empty">No jobs booked on this date.</p>
      ) : (
        <div className="mt-6 space-y-4">
          {jobs.map((j) => {
            const st = statusLabel(j, isContractor);
            return (
              <div key={j.job_id} className="border border-line bg-slate-100/70 p-6" data-testid={`calendar-job-${j.job_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <button onClick={() => onView(j.job_id)} className="font-display text-xl font-semibold tracking-tight text-blaze hover:underline" data-testid={`view-job-${j.job_id}`}>
                      {j.job_id}
                    </button>
                    <p className="mt-1 text-sm text-slate-700">{j.first_name} {j.last_name} — {j.start_time}</p>
                  </div>
                  <span className={`border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest ${st.cls}`} data-testid={`status-${j.job_id}`}>
                    {st.label}
                  </span>
                </div>
                <div className="mt-4 grid gap-2 text-xs text-slate-600 sm:grid-cols-2 lg:grid-cols-4">
                  <p>Pickup: <span className="text-slate-800">{[j.pickup_address, j.pickup_suburb].filter(Boolean).join(", ")}</span></p>
                  <p>Drop-off: <span className="text-slate-800">{[j.drop_address, j.drop_suburb].filter(Boolean).join(", ")}</span></p>
                  <p>Est: <span className="text-slate-800">{j.total_hours} hrs</span></p>
                  <p>Truck: <span className="text-slate-800">{j.truck}</span></p>
                </div>
                {(j.cbd_travel_min || 0) > 0 && (
                  <p className="mt-2 inline-block border border-amber-400/60 bg-amber-50 px-3 py-1.5 text-[11px] font-bold text-amber-700" data-testid={`cbd-badge-cal-${j.job_id}`}>
                    {Math.max(j.cbd_distance?.pickup_km || 0, j.cbd_distance?.drop_km || 0)} km from Melbourne CBD — +{j.cbd_travel_min === 60 ? "1 hour" : "30 min"} travel
                  </p>
                )}
                <div className="mt-5 flex flex-wrap items-center gap-4 border-t border-line/60 pt-5">
                  <div>
                    <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">Assign Movers (need {j.movers || 2})</p>
                    <div className="flex flex-wrap gap-2">
                      {movers.map((m) => {
                        const active = (selection[j.job_id] || []).includes(m.name);
                        return (
                          <button
                            key={m.id}
                            onClick={() => toggle(j.job_id, m.name)}
                            data-testid={`assign-${j.job_id}-${m.name.toLowerCase()}`}
                            className={`border px-4 py-2 text-xs font-semibold transition-all ${
                              active ? "border-blaze bg-blaze text-white" : "border-line text-slate-700 hover:border-blaze/60"
                            }`}
                          >
                            {m.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="ml-auto flex flex-wrap items-center gap-3">
                    <select
                      value={j.status}
                      onChange={(e) => setStatus(j.job_id, e.target.value)}
                      className="cursor-pointer border border-line bg-ink px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-700 outline-none"
                      data-testid={`jobstatus-${j.job_id}`}
                    >
                      {["booked", "confirmed", "in_progress", "completed", "cancelled"].map((s) => (
                        <option key={s} value={s} className="bg-coal text-slate-900">{s.replace("_", " ")}</option>
                      ))}
                    </select>
                    <button onClick={() => assign(j.job_id)} className="bg-blaze px-5 py-2 text-xs font-bold uppercase tracking-widest text-white" data-testid={`assign-btn-${j.job_id}`}>
                      Assign Job
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
